import { useCallback, useState } from 'react';
import { RefreshType, SurveyDiffResult } from '@features/survey-templates/types';
import { useLazyGetRefreshDiffQuery } from '@api/survey-templates-history';
import { toastFromError } from '@g17eco/molecules/toasts';

interface UseRefreshWithConfirmationParams {
  templateId: string;
  onRefresh: (params: { historyId: string; refreshType: RefreshType }) => void;
}

interface ConfirmationData {
  historyId: string;
  refreshType: RefreshType;
  modifiedSurveys: SurveyDiffResult[];
}

export const useRefreshWithConfirmation = ({ templateId, onRefresh }: UseRefreshWithConfirmationParams) => {
  const [getRefreshDiff, { isFetching: isCheckingDiff }] = useLazyGetRefreshDiffQuery();
  const [confirmationData, setConfirmationData] = useState<ConfirmationData | undefined>();

  const initiateRefresh = useCallback(
    async ({ historyId, refreshType }: { historyId: string; refreshType: RefreshType }) => {
      try {
        const { modifiedSurveys } = await getRefreshDiff({ templateId, historyId, refreshType }).unwrap();

        if (modifiedSurveys.length > 0) {
          setConfirmationData({
            historyId,
            refreshType,
            modifiedSurveys,
          });
        } else {
          onRefresh({ historyId, refreshType });
        }
      } catch (error) {
        toastFromError(error);
      }
    },
    [templateId, getRefreshDiff, onRefresh],
  );

  const confirmRefresh = useCallback(() => {
    if (confirmationData) {
      onRefresh({
        historyId: confirmationData.historyId,
        refreshType: confirmationData.refreshType,
      });
      setConfirmationData(undefined);
    }
  }, [confirmationData, onRefresh]);

  const cancelRefresh = useCallback(() => {
    setConfirmationData(undefined);
  }, []);

  return {
    initiateRefresh,
    confirmRefresh,
    cancelRefresh,
    isCheckingDiff,
    confirmationData,
  };
};
